import { useCallback, useEffect, useMemo, useState } from 'react';
import { Search, CircleDollarSign } from 'lucide-react';
import AppLayout from '../components/AppLayout';
import PetanqueLoader from '../components/PetanqueLoader';
import InvoiceDetailDialog from '../components/InvoiceDetailDialog';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { api } from '../api/sangly';
import { money, fmtDate, timeAgo } from '../lib/format';

export default function History() {
  const [q, setQ] = useState('');
  const [allPaid, setAllPaid] = useState(false);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [users, setUsers] = useState([]);

  const params = useMemo(() => {
    const p = { status: allPaid ? 'paid' : 'history', ordering: '-due', limit: 100 };
    if (q.trim()) p.q = q.trim();
    return p;
  }, [q, allPaid]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.invoices.list(params);
      setRows(data.results || []); setCount(data.count || 0);
    } finally { setLoading(false); }
  }, [params]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.users.list().then(setUsers).catch(() => {}); }, []);

  return (
    <AppLayout title="History"
               description="Followed-up invoices that have since been paid">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <label className="flex cursor-pointer items-center gap-1.5 text-[12px] text-muted-foreground">
          <input type="checkbox" checked={allPaid} onChange={(e) => setAllPaid(e.target.checked)}
                 className="h-3.5 w-3.5 rounded border-border accent-[hsl(var(--primary))]" />
          Include all paid bills (not just followed-up)
        </label>
        <div className="relative ml-auto">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search supplier / ref…" className="h-9 w-64 pl-8" />
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-card shadow-card">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Supplier / invoice</TableHead>
              <TableHead>Due</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Followed by</TableHead>
              <TableHead>Paid detected</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={5} className="h-40 text-center">
                <PetanqueLoader size={34} roll className="mx-auto" />
              </TableCell></TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={5} className="h-40 text-center text-muted-foreground">
                Nothing here yet - paid follow-ups will appear here.
              </TableCell></TableRow>
            )}
            {!loading && rows.map((inv) => (
              <TableRow key={inv.id} className="cursor-pointer" onClick={() => setSelected(inv.id)}>
                <TableCell>
                  <div className="font-medium leading-tight">{inv.supplier || '-'}</div>
                  <div className="text-[11px] text-muted-foreground"><span className="font-mono">{inv.name}</span>{inv.ref ? ` · ${inv.ref}` : ''}</div>
                </TableCell>
                <TableCell>{fmtDate(inv.date_due)}</TableCell>
                <TableCell className="text-right font-semibold tnum">{money(inv.amount_total, inv.currency)}</TableCell>
                <TableCell>{inv.flagged_by_name || <span className="text-muted-foreground">-</span>}</TableCell>
                <TableCell>
                  <Badge variant="success"><CircleDollarSign className="h-3 w-3" /> Paid</Badge>
                  {inv.paid_detected_at && <span className="ml-2 text-[11px] text-muted-foreground">{timeAgo(inv.paid_detected_at)}</span>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="mt-3 text-[12px] text-muted-foreground">{count.toLocaleString('nl-BE')} result{count === 1 ? '' : 's'}</div>

      <InvoiceDetailDialog invoiceId={selected} open={!!selected} onClose={() => setSelected(null)}
                           onChanged={load} users={users} />
    </AppLayout>
  );
}
